<template>
  <div class="container mt-4">
    <!-- État de chargement -->
    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
    </div>

    <!-- Erreur -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Contenu de la carte -->
    <div v-else class="card mb-4 shadow-sm">
      <div class="row g-0">
        <!-- Image Pixel Art -->
        <div class="col-md-4 d-flex align-items-center justify-content-center bg-light pixel-art-container">
          <img :src="mosaic.image" alt="Pixel Art" class="pixel-art-img" />
        </div>
        
        <!-- Détails -->
        <div class="col-md-8">
          <div class="card-body">
            <!-- Nom de la mosaïque -->
            <h5 class="card-title mb-3">{{ mosaic.name }}</h5>
            
            <!-- Auteur -->
            <div class="mb-3">
              <h6 class="text-muted mb-1">Créé par</h6>
              <p class="lead mb-0">
                <strong>{{ mosaic.author }}</strong>
              </p>
            </div>
            
            <!-- Description -->
            <div class="mb-3">
              <h6 class="text-muted mb-1">Description</h6>
              <p class="card-text">
                {{ mosaic.content || 'Aucune description disponible.' }}
              </p>
            </div>
            
            <!-- Date de création -->
            <p class="card-text mt-3">
              <small class="text-muted">
                📅 Publié le {{ mosaic.creationDate }}
              </small>
            </p>

            <!-- Badge publique -->
            <div class="mt-3">
              <span class="badge bg-success">🌍 Publique</span>
            </div>

            <!-- Bouton retour -->
            <div class="mt-4">
              <router-link to="/place" class="btn btn-outline-primary">
                ← Retour à la place publique
              </router-link>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Section Commentaires -->
      <div class="card-footer bg-white border-top">
        <h6 class="mb-3">💬 Commentaires ({{ comments.length }})</h6>

        <!-- Formulaire d'ajout de commentaire -->
        <div v-if="isAuthenticated" class="mb-4">
          <h6 class="mb-2">Ajouter un commentaire</h6>
          <form @submit.prevent="addComment">
            <textarea 
              v-model="newComment" 
              class="form-control mb-2" 
              rows="3"
              placeholder="Écrivez votre commentaire..."
              :disabled="postingComment"
            ></textarea>
            <button 
              type="submit" 
              class="btn btn-primary btn-sm"
              :disabled="!newComment.trim() || postingComment"
            >
              <span v-if="postingComment" class="spinner-border spinner-border-sm me-1" role="status"></span>
              <span v-else>📤</span> Publier
            </button>
          </form>
        </div>

        <!-- Message pour les non-connectés -->
        <div v-else class="alert alert-info mb-4">
          <router-link to="/login">Connectez-vous</router-link> pour laisser un commentaire.
        </div>

        <!-- État de chargement des commentaires -->
        <div v-if="loadingComments" class="text-center p-3">
          <div class="spinner-border spinner-border-sm text-secondary" role="status">
            <span class="visually-hidden">Chargement des commentaires...</span>
          </div>
        </div>

        <!-- Aucun commentaire -->
        <div v-else-if="comments.length === 0" class="text-muted text-center p-3">
          Aucun commentaire pour le moment. Soyez le premier à commenter !
        </div>

        <!-- Liste des commentaires -->
        <div v-else>
          <div 
            v-for="comment in comments" 
            :key="comment.id"
            class="mb-3 comment-item"
          >
            <div class="d-flex align-items-start">
              <div class="flex-shrink-0">
                <!-- Photo de profil si elle existe -->
                <img 
                  v-if="comment.profilePicture" 
                  :src="comment.profilePicture" 
                  :alt="comment.author"
                  class="profile-picture-comment"
                />
                <!-- Avatar avec initiale sinon -->
                <div v-else class="avatar-circle">{{ getInitial(comment.author) }}</div>
              </div>
              <div class="flex-grow-1 ms-3">
                <div class="d-flex justify-content-between align-items-start mb-1">
                  <div>
                    <strong>{{ comment.author }}</strong>
                    <span v-if="isMyComment(comment)" class="badge bg-info ms-2">Vous</span>
                  </div>
                  <!-- Boutons modifier/supprimer (uniquement pour ses propres commentaires) -->
                  <div v-if="isMyComment(comment)" class="btn-group btn-group-sm">
                    <button 
                      v-if="editingCommentId !== comment.id"
                      @click="startEditComment(comment)" 
                      class="btn btn-outline-secondary btn-sm"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button 
                      @click="deleteComment(comment.id)" 
                      class="btn btn-outline-danger btn-sm"
                      :disabled="deletingCommentId === comment.id"
                      title="Supprimer"
                    >
                      <span v-if="deletingCommentId === comment.id" class="spinner-border spinner-border-sm" role="status"></span>
                      <span v-else>🗑️</span>
                    </button>
                  </div>
                </div>
                
                <!-- Affichage normal -->
                <div v-if="editingCommentId !== comment.id">
                  <p class="mb-1">{{ comment.content }}</p>
                  <small class="text-muted">{{ formatDate(comment.datePosted) }}</small>
                </div>
                
                <!-- Mode édition -->
                <div v-else class="mt-2">
                  <textarea 
                    v-model="editCommentContent" 
                    class="form-control mb-2" 
                    rows="3"
                    placeholder="Modifiez votre commentaire..."
                  ></textarea>
                  <div class="d-flex gap-2">
                    <button @click="saveEditComment(comment.id)" class="btn btn-success btn-sm" :disabled="savingComment || !editCommentContent.trim()">
                      <span v-if="savingComment" class="spinner-border spinner-border-sm me-1" role="status"></span>
                      <span v-else>✓</span> Valider
                    </button>
                    <button @click="cancelEditComment" class="btn btn-secondary btn-sm">
                      ✕ Annuler
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'PixelArtCardPublic',
  data() {
    return {
      mosaic: null,
      loading: true,
      error: null,
      comments: [],
      loadingComments: false,
      newComment: '',
      postingComment: false,
      editingCommentId: null,
      editCommentContent: '',
      savingComment: false,
      deletingCommentId: null,
      currentUserProfileId: null,
      currentUserProfilePicture: null,
      currentUserNickname: null
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated'])
  },
  mounted() {
    this.fetchMosaic();
    this.fetchComments();
    
    // Récupérer l'ID du profil de l'utilisateur connecté
    if (this.isAuthenticated) {
      this.fetchCurrentUserProfile();
    }
  },
  methods: {
    async fetchMosaic() {
      try {
        const mosaicId = this.$route.params.id;
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        const response = await axios.get(`${API_URL}/api/mosaics/public/${mosaicId}`);
        
        this.mosaic = response.data;
        this.loading = false;
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        
        if (err.response?.status === 404) {
          this.error = 'Cette mosaïque n\'existe pas ou n\'est pas publique';
        } else if (err.response?.status === 403) {
          this.error = 'Cette mosaïque est privée';
        } else {
          this.error = 'Impossible de charger la mosaïque';
        }
        
        this.loading = false;
      }
    },

    async fetchComments() {
      this.loadingComments = true;
      try {
        const mosaicId = this.$route.params.id;
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        const response = await axios.get(`${API_URL}/api/mosaics/${mosaicId}/comments`);
        
        this.comments = response.data;
        this.loadingComments = false;
      } catch (err) {
        console.error('Erreur lors du chargement des commentaires:', err);
        this.loadingComments = false;
      }
    },

    async fetchCurrentUserProfile() {
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        const response = await axios.get(`${API_URL}/api/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        this.currentUserProfileId = response.data.profileId;
        this.currentUserProfilePicture = response.data.profilePicture;
        this.currentUserNickname = response.data.nickname;

        console.log('Profile ID actuel:', this.currentUserProfileId);
      } catch (err) {
        console.error('Erreur profil:', err);
      }
    },

    async addComment() {
        if (!this.newComment.trim()) return;
          
        this.postingComment = true;
          
        try {
          const token = localStorage.getItem('token');
          const mosaicId = this.$route.params.id;
          const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
          const response = await axios.post(
            `${API_URL}/api/mosaics/${mosaicId}/comments`,
            {
              content: this.newComment
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          // ✅ DÉCOMMENTER ET UTILISER CE CODE
          const newCommentData = {
            id: response.data.comment.id,
            content: response.data.comment.content,
            datePosted: response.data.comment.datePosted,
            author: response.data.comment.author || this.currentUserNickname,
            profileId: response.data.comment.profileId || this.currentUserProfileId,
            profilePicture: response.data.comment.profilePicture || this.currentUserProfilePicture
          };
        
          console.log('Nouveau commentaire:', newCommentData);
        
          // ✅ UTILISER newCommentData AU LIEU DE response.data.comment
          this.comments.unshift(newCommentData);
        
          // Stocker l'ID du profil si pas encore fait
          if (!this.currentUserProfileId && newCommentData.profileId) {
            this.currentUserProfileId = newCommentData.profileId;
          }
        
          this.newComment = '';
        
        } catch (error) {
          console.error('Erreur lors de l\'ajout du commentaire:', error);
          alert('Impossible d\'ajouter le commentaire');
        } finally {
          this.postingComment = false;
        }
    },

    isMyComment(comment) {
      // Vérifier si c'est le commentaire de l'utilisateur connecté
      return this.isAuthenticated && 
             this.currentUserProfileId !== null && 
             comment.profileId === this.currentUserProfileId;
    },

    startEditComment(comment) {
      this.editingCommentId = comment.id;
      this.editCommentContent = comment.content;
    },

    cancelEditComment() {
      this.editingCommentId = null;
      this.editCommentContent = '';
    },

    async saveEditComment(commentId) {
      if (!this.editCommentContent.trim()) {
        alert('Le commentaire ne peut pas être vide');
        return;
      }

      this.savingComment = true;

      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        await axios.post(
          `${API_URL}/api/comments/${commentId}`,
          {
            content: this.editCommentContent
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // Mettre à jour localement
        const comment = this.comments.find(c => c.id === commentId);
        if (comment) {
          comment.content = this.editCommentContent;
        }

        this.editingCommentId = null;
        this.editCommentContent = '';

      } catch (error) {
        console.error('Erreur lors de la modification:', error);
        alert('Impossible de modifier le commentaire');
      } finally {
        this.savingComment = false;
      }
    },

    async deleteComment(commentId) {
      if (!confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) {
        return;
      }

      this.deletingCommentId = commentId;

      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        await axios.delete(
          `${API_URL}/api/comments/${commentId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        // Retirer le commentaire de la liste
        this.comments = this.comments.filter(c => c.id !== commentId);

      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Impossible de supprimer le commentaire');
      } finally {
        this.deletingCommentId = null;
      }
    },

    getInitial(name) {
      return name ? name.charAt(0).toUpperCase() : '?';
    },

    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'À l\'instant';
      if (diffMins < 60) return `Il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
      if (diffHours < 24) return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
      if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
      
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.pixel-art-container {
  min-height: 400px;
  padding: 20px;
}

.pixel-art-img {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 500px;
  object-fit: contain;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  -ms-interpolation-mode: nearest-neighbor;
}

.card-footer {
  padding: 1.5rem 1.25rem;
}

.lead {
  font-size: 1.25rem;
  font-weight: 400;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #4dc9ff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
}

.profile-picture-comment {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #4dc9ff;
}

.comment-item {
  padding: 10px;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.comment-item:hover {
  background-color: #f8f9fa;
}

.gap-2 {
  gap: 0.5rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

@media (min-width: 768px) {
  .pixel-art-container {
    min-height: 500px;
  }
  
  .pixel-art-img {
    max-height: 600px;
  }
}

@media (min-width: 1200px) {
  .pixel-art-container {
    min-height: 600px;
  }
  
  .pixel-art-img {
    max-height: 700px;
  }
}
</style>